'use client'

import { useCallback } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import { Geo } from '@aws-amplify/geo'
import { countryCodes } from '@skop-app/lib-domain/constants'

export type AddressOption = {
  label: string | undefined
  address: string
  city: string | undefined
  zipcode: string | undefined
  country: string | undefined
  location?: {
    lat: number
    lon: number
  }
}

type UseAddressAutocompleteOptions = {
  countries?: string[]
  maxResults?: number
}

export const useAddressAutocomplete = (
  options: UseAddressAutocompleteOptions = {},
) => {
  const { countries = ['FRA', 'BEL', 'LUX', 'CHE', 'NLD'], maxResults = 5 } =
    options

  const searchAddressByText = useDebouncedCallback(
    async (
      inputValue: string,
      callback: (options: AddressOption[]) => void,
    ) => {
      try {
        const places = await Geo.searchByText(inputValue, {
          countries,
          maxResults,
        })
        const result = places.map((place) => {
          const point = place?.geometry?.point
          const hasValidCoordinates =
            typeof point?.[0] === 'number' && typeof point?.[1] === 'number'

          return {
            label: place.label,
            address: place?.street
              ? [place.addressNumber, place.street].filter(Boolean).join(' ')
              : (place.label ?? ''),
            city: place?.municipality,
            zipcode: place?.postalCode,
            country: countryCodes.find((c) => c.value === place?.country)
              ?.label,
            ...(hasValidCoordinates
              ? {
                  location: {
                    lat: point[1],
                    lon: point[0],
                  },
                }
              : {}),
          }
        })

        callback(result)
      } catch (error) {
        console.error(error)
        callback([])
      }
    },
    350,
  )

  const loadOptions = useCallback(
    (inputValue: string, callback: (options: AddressOption[]) => void) => {
      return new Promise(() => searchAddressByText(inputValue, callback))
    },
    [searchAddressByText],
  )

  return { loadOptions }
}
